"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const caseStudies = [{
  id: 1,
  title: "22% Savings in Pharma Packaging",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_pharma.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_pharma.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_pharma.png-wvc-sizes",
  imageAlt: "Macro shot of glass vials used in pharmaceutical packaging",
  colSpanClass: "md:col-span-2"
}, {
  id: 2,
  title: "15% Cost Reduction for Consumer Brand",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_fb.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_fb.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_fb.png-wvc-sizes",
  imageAlt: "Close up texture of corrugated board packaging",
  colSpanClass: "md:col-span-1"
}, {
  id: 3,
  title: "Logistics Optimization in Luxury Goods",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_logistics_luxury.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_logistics_luxury.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_logistics_luxury.png-wvc-sizes",
  imageAlt: "Luxury goods being prepared for transport in optimized rigid packaging",
  colSpanClass: "md:col-span-1"
}, {
  id: 4,
  title: "18% Margin Increase via Supplier Change",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_industrial.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_industrial.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678509/img/case_study_industrial.png-wvc-sizes",
  imageAlt: "Industrial steel and aluminum packaging components",
  colSpanClass: "md:col-span-2"
}];
const caseStudyMaterials = [{
  caseStudyId: 1,
  material: "Glass"
}, {
  caseStudyId: 1,
  material: "Flexible Plastics"
}, {
  caseStudyId: 2,
  material: "Corrugated"
}, {
  caseStudyId: 2,
  material: "Solid Board"
}, {
  caseStudyId: 3,
  material: "Rigid Plastics"
}, {
  caseStudyId: 3,
  material: "Glass"
}, {
  caseStudyId: 4,
  material: "Steel & Aluminum"
}];
export default function CaseStudies() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="case-studies" className="py-24 lg:py-32 bg-muted">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 max-w-3xl">
          <h2 className="font-default text-3xl font-bold tracking-tight text-primary sm:text-4xl">
            Proven Results Across Industries
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            A curated selection of our strategic sourcing achievements, highlighting significant cost reductions and supplier optimizations.
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 gap-8 md:grid-cols-3 transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          {caseStudies.map((study, i) => {
          const materials = caseStudyMaterials.filter(m => m.caseStudyId === study.id);
          return <div key={study.id} data-index={i} className={study.colSpanClass}>
                <Card className="group h-full overflow-hidden border-border bg-card shadow-sm transition-all duration-300 hover:shadow-md">
                  <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted md:aspect-[16/10]">
                    <img src={study.imageSrc} data-wvc-srcset={study.imageSrcset} data-wvc-sizes={study.imageSizes} alt={study.imageAlt} loading="lazy" className="h-full w-full object-cover grayscale transition-transform duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                  </div>
                  <CardHeader className="pb-4 pt-6">
                    <CardTitle className="font-default text-xl font-semibold text-primary md:text-2xl">
                      {study.title}
                    </CardTitle>
                  </CardHeader>
                  {i === 0 && <div className="px-6 mb-2">
                      <p className="text-sm italic text-muted-foreground">
                        Reduced costs through supplier rationalization and specification harmonization
                      </p>
                    </div>}
                  {i === 1 && <div className="px-6 mb-2">
                      <p className="text-sm italic text-muted-foreground">
                        Achieved leverage through vertical integration of base materials.
                      </p>
                    </div>}
                  {i === 2 && <div className="px-6 mb-2">
                      <p className="text-sm italic text-muted-foreground">
                        Optimized transportation flows to eliminate unnecessary waste.
                      </p>
                    </div>}
                  {i === 3 && <div className="px-6 mb-2">
                      <p className="text-sm italic text-muted-foreground">
                        Unlocked margin improvement by scouting and qualifying competitive international suppliers.
                      </p>
                    </div>}
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {materials.map((mat, matIndex) => <Badge key={`${study.id}-${matIndex}`} data-index={matIndex} variant="secondary" className="bg-muted text-[12px] font-medium text-muted-foreground hover:bg-muted">
                          {mat.material}
                        </Badge>)}
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}